import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import { Drawer } from '@material-ui/core';
import { useTranslation } from 'utils/modifiedTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { authenticationActions } from 'components/Authentication/actions';
import { OTHER_DISEASE, DISEASE, DISEASES } from 'utils/constants/disease';
import { POPUP } from 'utils/constants/popup';
import { noop } from 'functions/noop';
import DownloadIcon from 'assets/icons/_download.svg';
import PlayIcon from 'assets/icons/play.svg';
import EditIcon from 'assets/icons/edit.svg';
import PrintIcon from 'assets/icons/_print.svg';
import arrowCircleRight from 'assets/icons/arrow-circle-right.svg';
import VIcon from 'assets/icons/v.svg';
import XIcon from 'assets/icons/x.svg';
import Icon from 'assets/icons/profile.svg';
import Tooltip from 'assets/icons/tooltip.png';
import TooltipLamp from 'assets/icons/tooltip_lamp.svg';
import { getUserConditionProfile, getUserCondition } from 'modules/user/utils';
import app from 'new-ui/app';
import { profileTreatmentSectionEvents, GA_CATEGORY_NAME } from 'modules/ga/profileSectionEvents';
import { SurveyAnswerKeys, SurveyTypes } from 'modules/survey/surveyEnums';
import { DAY_IN_MS } from 'new-ui/constants';
import ScrollTabs from 'new-ui/Components/ScrollTabs';
import LanguageMenu from 'new-ui/Components/LanguageMenu';
import {
  PopupContent, PopupProperty, PopupButton,
} from 'new-ui/Components/Popup';
import { ProfileTreatments } from 'new-ui/Components/ProfileTreatments/ProfileTreatments';
import { sortTreatmentByLatest } from 'new-ui/functions/sortTreatmentByLatest';
import {
  filterQuestionsByCondition, hasGenderQuestion,
} from 'new-ui/functions';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import { TREATMENT_QUESTION_ID_BY_DISEASE } from 'modules/questionnaire/constants';
import { SIDEBAR_POSITIONS, TRANSITION_DURATION } from 'components/Sidebar/sidebarSettings';
import { getQuestionnaireRoute } from 'functions/getQuestionnaireRoute';
import { getFormattedProfile, downloadProfilePDF } from 'modules/matches/api';
import { printProfilePDF } from 'modules/matches/helpers';
import { hideBackdrop, showBackdrop } from 'modules/backdrop/actions';
import AiAssistant from 'new-ui/Components/AiAssistent';
import { getAge } from 'functions/getAge';
import { getSurvey } from 'modules/survey/api';
import { getIntakeUnansweredNum } from 'new-ui/Components/Intake';
import { getCleanedTreatments } from 'new-ui/Components/QuestionRenderer/renderers/TreatmentQuestion';
import { flashIcon } from './functions';
import { getUnreadActionsNum, getProfileActions } from './profileActions';
import PhoneNumber from '../PhoneNumber';
import LocationSelector from '../LocationSelector/LocationSelector';
import './MedicalProfilePopup.css';

const BACKDROP_REASON = 'medical_profile_popup';

const {
  profileCriticalInformationPresented,
  profileCriticalInformationClicked,
  profileCriticalInformationAnswered,
} = profileTreatmentSectionEvents;

const PARTS = [
  'diseaseCharacteristics',
  'proceduresAndSurgeries',
  'treatmentHistory',
  'comorbiditiesAndGeneralHealthInformation',
];

const CONDITIONS_TO_EXCLUDE_SURVEY = [
  DISEASE.RCC,
  DISEASE.MESO,
  DISEASE.GENERAL,
  DISEASE.CML,
  OTHER_DISEASE,
  DISEASE.SARCOMA,
  DISEASE.CERVICAL,
];

export const MedicalProfile = ({ highlight }) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.main.lang);
  const [drawerContent, setDrawerContent] = useState(null);
  const [profile, setProfile] = useState(null);
  const { t } = useTranslation();

  const [showTreatmentsAlert, setShowTreatmentsAlert] = useState(false);
  const [myDetailsAlert, setMyDetailsAlert] = useState(false);
  const [showSurveyBtn, setShowSurveyBtn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tooltipInfoVisible, setTooltipInfoVisible] = useState(false);
  const [filteredProfile, setFilteredProfile] = useState({});
  const [showTreatmentsQuestion, setShowTreatmentsQuestion] = useState(false);

  const condition = getUserCondition(app.user);

  const { treatment_for_advanced_metastatic_disease } = app?.user?.condition_profile || {};
  const treatments = getCleanedTreatments(app?.user?.condition_profile?.treatments || []);

  const [lastTreatment, ...previousTreatments] = sortTreatmentByLatest(treatments || []);
  const lastTreatmentDrug = lastTreatment?.drugs_received?.sort()?.join('+');
  let missedQuestions = [
    ...new Set(app.trials.reduce((a, b) => a.concat(b.missed_questions), [])),
    ...new Set(app.treatments ? app.treatments.reduce((a, b) => a.concat(b.missed_questions), []) : null),
  ];
  missedQuestions = [...new Set(missedQuestions)];
  let missedPartsAlert = 0;
  useEffect(() => {
    if (profile && Object.keys(filteredProfile).length) {
      app.sendGoogleAnalyticsEvent(
        GA_CATEGORY_NAME,
        profileCriticalInformationPresented.eventName,
        profileCriticalInformationPresented.getMetaData(missedPartsAlert > 0),
      );
    }
    // eslint-disable-next-line
  }, [profile, filteredProfile]);

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);

      const promises = [];

      // !app.user should be invariant
      if (app.user && !app.questions) {
        promises.push(app.initQuestions(app.user.personal.condition, app.user.condition_profile));
      } else {
        promises.push(Promise.resolve());
      }

      if (lastTreatmentDrug) {
        promises.push(getSurvey(SurveyTypes.treatmentExperience, app.user.info.user_wix_id));
      } else {
        promises.push(Promise.resolve());
      }
      const results = await Promise.all(promises);

      if (!lastTreatmentDrug) {
        setIsLoading(false);
        return;
      }
      const [, r] = results;
      const answers = r?.data?.answers || [];
      let hasAnswer = false;
      answers.forEach((ans) => {
        if (ans?.surveyKey === lastTreatmentDrug && ans.key === SurveyAnswerKeys.sideEffects) {
          hasAnswer = true;
        }
      });
      if (hasAnswer && !CONDITIONS_TO_EXCLUDE_SURVEY.includes(condition)) {
        setShowSurveyBtn(false);
      } else if (!hasAnswer && !CONDITIONS_TO_EXCLUDE_SURVEY.includes(condition)) {
        setShowSurveyBtn(true);
      }
      setIsLoading(false);
    };
    fetch();
  }, [condition, lastTreatmentDrug]);

  useEffect(() => {
    const getProfile = async () => {
      const r = await getFormattedProfile(null, lang, true);
      setProfile(r.result);
      const $prop = document.querySelector('.popup-property.highlighted');

      if ($prop) flashIcon(VIcon, $prop);
    };
    getProfile();
  }, [lang]);

  useEffect(() => {
    const filterParts = async () => {
      const filteredP = {};
      const user = app?.user || {};
      const { condition_profile } = user;
      const filterTreatments = await filterQuestionsByCondition({
        questionsIds: ['treatments'],
        condition: getUserCondition(user),
        conditionProfile: getUserConditionProfile(user),
      });
      await Promise.all(PARTS.map(async (name) => {
        if (!profile?.[name]) return null;

        await Promise.all(profile[name].map(async (part) => {
          if (typeof part !== 'string' && part.formatType) {
            if (!filteredP[name]) {
              filteredP[name] = [part];
            } else {
              filteredP[name] = [...filteredP[name], part];
            }
            return;
          }
          let value = part.slice(part.indexOf(':') + 2);
          let question = null;
          const valueParts = value.split('&&');

          if (valueParts.length === 2) {
            [value, question] = valueParts;
            const filteredQuestion = await filterQuestionsByCondition({
              questionsIds: [question],
              condition: getUserCondition(app?.user),
              conditionProfile: getUserConditionProfile(app?.user),
            });
            if (filteredQuestion.length) {
              if (!filteredP[name]) {
                filteredP[name] = [part];
              } else {
                filteredP[name] = [...filteredP[name], part];
              }
            }
            return;
          }

          if (!filteredP[name]) {
            filteredP[name] = [part];
          } else {
            filteredP[name] = [...filteredP[name], part];
          }
        }));
        return null;
      }));
      let showForNewTreatments = false;
      const treatmentForIndicationQuestionId = TREATMENT_QUESTION_ID_BY_DISEASE[user?.personal?.condition];
      const treatmentForIndicationQuestion = user?.condition_profile[treatmentForIndicationQuestionId];
      if (treatmentForIndicationQuestion === 'no' && (
        !user?.personal.answered_no_on_has_treatment_date
        || Date.now() - user?.personal.answered_no_on_has_treatment_date > DAY_IN_MS
      ) && condition_profile?.hasOwnProperty('treatments')) {
        showForNewTreatments = true;
      }
      setShowTreatmentsQuestion((!!filterTreatments.length && condition_profile?.hasOwnProperty('treatments')) || showForNewTreatments);
      setFilteredProfile(filteredP);
    };
    if (profile) {
      filterParts();
    }
  }, [profile]);

  const showPreloader = isLoading || !profile;
  useEffect(() => {
    if (showPreloader) {
      dispatch(showBackdrop({ reason: BACKDROP_REASON }));
    }
    return () => {
      dispatch(hideBackdrop({ reason: BACKDROP_REASON }));
    };
  }, [dispatch, showPreloader]);

  const goToDiseaseStatus = () => {
    dispatch(push(getQuestionnaireRoute({ questionId: QUESTION_ID.disease_status })));
  };
  const edit = () => {
    app.setPopup(false);
    goToDiseaseStatus();
  };
  const logout = () => {
    dispatch(authenticationActions.logout());
  };
  const startNew = () => {
    // broken
    app.setPopup(false);
    // dispatch(authenticationActions.newProfile());
    goToDiseaseStatus();
  };
  const printMedicalProfile = () => {
    app.showPreloader();
    printProfilePDF('en').catch(noop).finally(() => {
      app.hidePreloader();
    });
  };
  const downloadMedicalProfile = () => {
    app.showPreloader();
    downloadProfilePDF('en').catch(noop).finally(() => {
      app.hidePreloader();
    });
  };
  const gotoQuestionnaire = (question, onEdit) => {
    setTimeout(() => {
      let edited = false;
      app.editQuestion(
        [question],
        async ({ notFound }) => {
          edited = true;
          if (notFound) {
            // should be invariant
            console.error(`${question} question is not found`);
            app.setPopup(false);
            return;
          }

          app.userSignupInteracted = true;
          if (onEdit) { onEdit(); }
          await app.requestRematch();
          app.setPopup(POPUP.MEDICAL_PROFILE, {
            highlight: question,
          });
        },
      );

      app.onpopupclose = () => {
        if (edited) {
          return;
        }
        Promise.resolve().then(() => {
          app.setPopup(POPUP.MEDICAL_PROFILE, {
            highlight: question,
          });
        });
      };
    }, 10);
  };

  const getTreatments = () => (
    <ProfileTreatments
      showSurveyBtn={showSurveyBtn}
      showTreatmentsAlert={showTreatmentsAlert}
      setShowTreatmentsAlert={setShowTreatmentsAlert}
      gotoQuestionnaire={gotoQuestionnaire}
      setDrawerContent={setDrawerContent}
      profile={app?.user?.condition_profile}
      treatment_for_advanced_metastatic_disease={treatment_for_advanced_metastatic_disease}
      lastTreatment={lastTreatment}
      previousTreatments={previousTreatments}
      treatments={treatments || []}
      lastTreatmentDrug={lastTreatmentDrug}
    />
  );

  const handleMedicalInfo = () => {
    setTooltipInfoVisible((v) => !v);
  };

  const getMedicalInformation = () => {
    const bodyContentArr = [];
    const missedBodyParts = [];

    let alerts = 0;

    PARTS.forEach(async (name, key) => {
      if (!filteredProfile?.[name]) return null;
      const bodyMainContent = [];
      const bodyMissedContent = [];

      filteredProfile[name].map(async (part, k) => {
        if (typeof part !== 'string' && part.formatType) {
          if (part.values.length && part.values.length === 1 && part.values[0].includes('Unknown')) {
            return;
          }
          bodyMainContent.push((
            <div key={k} className="part-box" onClick={() => { gotoQuestionnaire('procedures'); }}>
              {part.values.map((v, key2) => <div key={key2} className="part-box-value">{v}</div>)}
            </div>
          ));
          return;
        }
        const title = part.slice(0, part.indexOf(':'));
        let value = part.slice(part.indexOf(':') + 2);
        let question = null;
        let editMenu = null;
        let onClick = null;
        const valueParts = value.split('&&');
        let propertyClass = 'profile-property';

        if (valueParts.length === 2) {
          [value, question] = valueParts;
          const isUnknown = (value === 'Unknown' || value.includes('null'));
          if (highlight && highlight === question) propertyClass += ' highlighted';
          editMenu = {
            title: isUnknown ? t('medical_profile.add') : null,
            icon: !isUnknown ? EditIcon : null,
          };
          if (isUnknown) {
            alerts += 1;
            propertyClass += ' empty';
            value = '';
          }

          onClick = () => {
            gotoQuestionnaire(question);
          };
          if (missedQuestions.includes(question) && isUnknown) {
            const onEdit = () => {
              app.sendGoogleAnalyticsEvent(
                GA_CATEGORY_NAME,
                profileCriticalInformationAnswered.eventName,
                profileCriticalInformationAnswered.getMetaData(),
              );
            };
            missedPartsAlert += 1;
            bodyMissedContent.push((
              <PopupProperty
                key={part + k + key}
                title={title}
                value={value}
                sideButton={{
                  action: () => {
                    app.sendGoogleAnalyticsEvent(
                      GA_CATEGORY_NAME,
                      profileCriticalInformationClicked.eventName,
                      profileCriticalInformationClicked.getMetaData(),
                    );
                    gotoQuestionnaire(question, onEdit);
                  },
                  title: isUnknown ? t('medical_profile.add') : null,
                  icon: !isUnknown ? EditIcon : null,
                }}
                className={propertyClass}
              />
            ));
            return;
          }
        }

        bodyMainContent.push((
          <PopupProperty
            onClick={onClick}
            key={part + k + key}
            title={title}
            value={value}
            sideButton={editMenu}
            className={propertyClass}
          />
        ));
      });

      bodyContentArr.push(
        ...bodyMainContent,
      );
      missedBodyParts.push(
        ...bodyMissedContent,
      );
      return null;
    });

    return {
      title: `${t('popup.navbar.my_info')}`,
      body: (
        <div className="part medical-information-wrapper">
          <div className="section-title-wrapper">
            {alerts > 0 && <span>{alerts}</span>}
            <h3>{t('popup.medical.my_info')}</h3>
          </div>
          {missedBodyParts.length > 0 && (
            <div className="section-missed-parts">
              <div className="section-title-wrapper">
                {missedPartsAlert > 0 && <span>{missedPartsAlert}</span>}
                <div className="title-wrapper">
                  <h3>Critical Information</h3>
                  <img
                    alt="tooltip"
                    src={Tooltip}
                    className="close"
                    onClick={handleMedicalInfo}
                  />
                </div>
              </div>
              <div className={`tooltip-wrapper ${tooltipInfoVisible ? 'visible' : ''}`}>
                <div className="title-close"><img onClick={handleMedicalInfo} src={XIcon} alt="tooltip close" /></div>
                <div className="title-content">
                  <img src={TooltipLamp} alt="tooltip img" />
                  <div>
                    <h3>{t('medical_profile.critical_tooltip.title')}</h3>
                    <p>
                      {t('medical_profile.critical_tooltip.body')}
                    </p>
                  </div>
                </div>
              </div>
              {missedBodyParts}
            </div>
          )}
          {bodyContentArr}
        </div>
      ),
      alert: alerts,
    };
  };

  const getMyDetails = () => {
    let alerts = 0;
    const questions = [
      {
        question: 'family_name',
        title: 'Name',
        val: app?.user?.info?.family_name,
        condition: true,
      },
      {
        title: 'Email',
        val: app?.user?.info?.email,
        condition: !!app?.user?.info?.email,
      },
      {
        question: 'phone_number',
        title: 'Phone',
        val: app?.user?.info?.phone,
        clickHandle: () => {
          setDrawerContent(
            <PhoneNumber
              closePopup={() => {
                setDrawerContent(null);
                app.setPopup(POPUP.MEDICAL_PROFILE);
              }}
            />,
          );
        },
        condition: true,
      },
      {
        question: 'preferred_location',
        title: 'Location',
        val: (!app?.user?.info?.country?.label || app?.user?.info?.country?.label === 'user location') ? null : app?.user?.info?.country?.label,
        clickHandle: () => {
          setDrawerContent(
            <LocationSelector
              closePopup={() => {
                setDrawerContent(null);
                app.setPopup(POPUP.MEDICAL_PROFILE);
              }}
            />,
          );
        },
        condition: true,
      },
      {
        question: 'travel_limit',
        title: 'Preferred distance',
        val: app?.user?.personal?.travel_limit,
        condition: true,
      },
      {
        question: 'dob',
        title: 'Age',
        val: app?.user?.personal?.dob && getAge(app?.user?.personal?.dob),
        condition: true,
      },
      {
        question: 'gender',
        title: 'Gender',
        val: app?.user?.personal?.gender,
        condition: hasGenderQuestion(),
      },
      {
        question: 'user_type',
        title: 'User type',
        val: app?.user?.info?.user_type,
        condition: true,
      },
      {
        question: 'insurances',
        title: 'Insurance',
        val: (() => {
          let insurances = app?.user?.condition_profile?.insurances;
          insurances = Array.isArray(insurances) ? insurances : insurances?.split(',');

          return insurances
            ? insurances.map(
              (ins) => t(`questionnaire.questions.insurances.${ins}`),
            ).join(' , ')
            : null;
        })(),
        condition: Array.isArray(app?.user?.condition_profile?.have_insurance)
          ? app?.user?.condition_profile?.have_insurance?.[0] === 'have_insurance_yes'
          : app?.user?.condition_profile?.have_insurance === 'have_insurance_yes',
      },
      {
        question: 'race',
        title: 'Race/ethnicity',
        val: app?.user?.condition_profile?.race?.length ? app?.user?.condition_profile?.race?.map(
          (val) => val.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' '),
        ).join(' , ') : null,
        condition: true,
      },
    ]
      .filter(({ condition }) => !!condition)
      .map(({
        question, title, val, clickHandle,
      }, k) => {
        let propertyClass = 'profile-property';
        let value = val;
        if (highlight && highlight === question) propertyClass += ' highlighted';
        const isUnknown = ((value === 'Unknown') || (value === null));
        let editMenuTitle = isUnknown ? t('medical_profile.add') : null;

        if (question === 'phone_number' && !isUnknown) {
          editMenuTitle = t('medical_profile.update');
        }

        const editMenu = {
          title: editMenuTitle,
          icon: !isUnknown && question !== 'phone_number' ? EditIcon : null,
        };
        if (isUnknown) {
          alerts += 1;
          propertyClass += ' empty';
          value = '';
        }
        const onClick = () => {
          if (clickHandle) {
            clickHandle();
          } else {
            gotoQuestionnaire(question);
          }
        };
        return (
          <PopupProperty
            onClick={question && onClick}
            key={k}
            title={title}
            value={value}
            sideButton={question && editMenu}
            className={propertyClass}
          />
        );
      });
    if (alerts && !myDetailsAlert) {
      setMyDetailsAlert(alerts);
    }
    return (
      <div className="my-details-wrapper">
        <div className="section-title-wrapper">
          {myDetailsAlert && <span>{myDetailsAlert}</span>}
          <h3>My details</h3>
        </div>
        {questions}
      </div>
    );
  };

  const getYourAttention = () => {
    const jsx = [];

    jsx.push(
      <div key="actions" className="your-attention-wrapper part actions">
        <div className="actions-title">
          {getUnreadActionsNum() > 0 && <span>{getUnreadActionsNum()}</span>}
          <p>
            {' '}
            {t('medical_profile.actions.title')}
            {' '}
          </p>
        </div>
        {getProfileActions()}
      </div>,
    );

    setTimeout(() => {
      if (app.setUnreadActions) app.setUnreadActions(0);
    }, 100);

    return jsx;
  };

  const getActions = () => (
    <div className="medical-profile-actions">
      <div className="medical-profile-system-language">
        <LanguageMenu fullWidth />
      </div>
      {condition !== OTHER_DISEASE && (
        <div className="popup-link download" onClick={printMedicalProfile}>
          <img src={PrintIcon} alt="PrintIcon" />
          {t('Print your medical profile')}
        </div>
      )}
      {condition !== OTHER_DISEASE && (
        <div className="popup-link download" onClick={downloadMedicalProfile}>
          <img src={DownloadIcon} alt="DownloadIcon" />
          {t('navbar.menu.item.download_medical_profile')}
        </div>
      )}
      {condition !== OTHER_DISEASE && (
        <div className="popup-link" onClick={startNew}>
          <img src={PlayIcon} alt="PlayIcon" />
          {t('navbar.menu.item.start_new_medical_profile')}
        </div>
      )}
      <div className="popup-link logout" onClick={logout}>
        {t('navbar.menu.item.log_out')}
      </div>
      {condition !== OTHER_DISEASE && (
        <PopupButton forwardIcon={arrowCircleRight} title={t('medical_profile.edit_medical_profile')} action={edit} />
      )}
    </div>
  );

  const tabs = [
    ...(showTreatmentsQuestion ? [{
      title: `${t('popup.navbar.my_treatments')}`,
      body: getTreatments(),
      alert: showTreatmentsAlert ? 1 : null,
      isShown: condition !== OTHER_DISEASE,
    }] : []),
    {
      ...getMedicalInformation(),
      isShown: condition !== OTHER_DISEASE,
    },
    {
      title: 'My details',
      body: getMyDetails(),
      alert: myDetailsAlert,
    },
    {
      title: `${t('popup.navbar.my_journey')}`,
      body: getYourAttention(),
      alert: getUnreadActionsNum(),
    },
    {
      body: getActions(),
    },
  ].filter(({ isShown = true }) => isShown);

  return (
    <div className="medical-profile-popup">
      <div className="mobile-only medical-profile-popup-header">
        <div className="icon-wrapper">
          <img alt="icon" src={Icon} />
        </div>
      </div>
      <PopupContent>
        <ScrollTabs
          header={DISEASES.includes(getUserCondition(app.user)) && getIntakeUnansweredNum(app.user) ? (
            <AiAssistant />
          ) : null}
          tabs={tabs}
        />
      </PopupContent>

      {drawerContent && (
        <Drawer
          transitionDuration={TRANSITION_DURATION}
          anchor={SIDEBAR_POSITIONS.TOP}
          PaperProps={{ style: { position: 'absolute' } }}
          BackdropProps={{ style: { position: 'absolute' } }}
          ModalProps={{
            container: document.querySelector('.medical_profile'),
            style: { position: 'absolute' },
          }}
          variant="temporary"
          open={!!drawerContent}
          onClose={() => { setDrawerContent(null); }}
        >
          <div className="medical-drawer-header">
            <img alt="close" src={XIcon} className="close" onClick={() => { setDrawerContent(null); }} />
          </div>
          <div className="medical-drawer-body">
            {drawerContent}
          </div>
        </Drawer>
      )}
    </div>
  );
};

MedicalProfile.propTypes = {
  highlight: PropTypes.string,
};
